/**
 * The Week grid (DNKADM-0300 — the single Week view: the design's FOLDED
 * schedule, replacing the Schedule/List/Heatmap/Load-bars layout toggle).
 * "The schedule is its own heatmap": each day's header card carries the single
 * {@link loadPercent} reading (booked lane-hours over that day's REAL
 * operating window, measured now→close on today, `—` when unmeasurable) with
 * its qualifier and the SERVICE MIX AS DOTS (a dot+count per service — colour
 * already means service, so the mix is never a second colour channel), and the
 * body below is the week's actual bookings as mini per-lane tick blocks.
 *
 * **Preserved behaviors:** the Week still clicks through to a Day (the day
 * header card is a button → `onOpenDay`); every tick block is a real button
 * that opens the detail surface (the spec's primary interaction — on every
 * day, not only today); pending ticks keep their dashed treatment; cancelled
 * ticks render dimmed and stay clickable; the live "now" marker renders on
 * today only; By-bay AND By-staff axes both work (the mini columns are the
 * active axis's lanes).
 *
 * **Track window:** the union of the seven days' operating windows (fail-soft
 * to the default when hours are unknown) — deliberately NOT re-widened by
 * out-of-hours bookings (that was the union rule this unit removes). A booking
 * outside the track clips out of the mini schedule; its day remains one tap
 * from the Day view, where the after-hours control lists it.
 */
import { type ReactNode } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { buildCalendarMaps } from '@/theme/maps/calendar';
import type { Reservation } from '@/api/reservations';
import type { WeeklyHoursDay } from '@/api/operatingHours';
import type { LaneBlock } from './DayGrid';
import { blockedAriaLabel, blockedBandSx } from './blockedBand';
import { blockedRangesOf } from '../blockOff';
import { todayIn } from '@/components/list/listDate';
import { weekdayLabel, ymdParts } from '@/utils/clockTime';
import {
  DEFAULT_HOUR_RANGE,
  deriveServiceColor,
  deriveStatusBucket,
  loadPercent,
  loadQualifier,
  localDateValueOf,
  localHourOf,
  nowHourFraction,
  operatingDayFor,
  toDateValue,
  weekDates,
  type HourRange,
  type ResourceAxis,
} from './derive';

type Mode = 'light' | 'dark';

/** Minimum tick height (px-ish, as a % floor is applied too) for tappability. */
const MIN_TICK_PERCENT = 3;

export interface WeekGridProps {
  reservations: Reservation[];
  /** Any `YYYY-MM-DD` inside the visible Mon–Sun week. */
  date: string;
  /** Open the detail surface for a booking — the primary interaction. */
  onSelect: (reservation: Reservation) => void;
  /** Switch to the Day view on a `YYYY-MM-DD` (the day-card click-through). */
  onOpenDay: (date: string) => void;
  /** The location's weekly schedule (null → the default window everywhere). */
  operatingDays: WeeklyHoursDay[] | null;
  /** The active resource axis (bays or staff) — the mini columns. */
  axis: ResourceAxis;
  /** The venue IANA zone (fail-soft null). */
  timeZone?: string | null;
  /**
   * The week's block-offs per lane and date (HOTFIX: the Day view drew them, the Week view
   * did not) — each a date exception's open shape; the grid renders what it CLOSES as a
   * non-interactive band in that day's mini lane, against the shared track, the same way
   * the Day grid does against its axis.
   */
  blocks?: DatedLaneBlock[];
  /** A tap on a Blocked band (DNKADM-0456 V-70), with the band's date. Absent → bands stay images. */
  onBlockSelect?: (block: LaneBlock, date: string) => void;
}

/** A {@link LaneBlock} on a given `YYYY-MM-DD`. */
export interface DatedLaneBlock extends LaneBlock {
  date: string;
}

/** `9a` / `12p`-style compact tick label. */
function compactHour(hour: number): string {
  const hour12 = hour % 12 || 12;
  return `${hour12}${hour >= 12 && hour < 24 ? 'p' : 'a'}`;
}

/** An operating day's whole-hour open→close, or null (mirrors the shell's rule). */
function windowOf(day: WeeklyHoursDay | null): HourRange | null {
  if (!day || !day.isAvailable || day.shifts.length === 0) return null;
  const parse = (t: string): number | null => {
    const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(t.trim());
    return m ? Number(m[1]) + Number(m[2]) / 60 : null;
  };
  let open: number | null = null;
  let close: number | null = null;
  for (const shift of day.shifts) {
    const s = parse(shift.startTime);
    const e = parse(shift.endTime);
    if (s !== null) open = open === null ? s : Math.min(open, s);
    if (e !== null) close = close === null ? e : Math.max(close, e);
  }
  if (open === null || close === null || close <= open) return null;
  return { startHour: Math.floor(open), endHour: Math.ceil(close) };
}

export function WeekGrid({
  reservations,
  date,
  onSelect,
  onOpenDay,
  operatingDays,
  axis,
  timeZone = null,
  blocks = [],
  onBlockSelect,
}: WeekGridProps) {
  const mode: Mode = useTheme().palette.mode === 'dark' ? 'dark' : 'light';
  const cm = buildCalendarMaps(mode);
  const today = todayIn(timeZone); // the venue calendar (DNKADM-0439)
  const nowHour = nowHourFraction(new Date(), timeZone);

  const days = weekDates(date).map(toDateValue);
  const windows = new Map<string, HourRange | null>(
    days.map((dv) => [dv, windowOf(operatingDayFor(operatingDays, dv))]),
  );

  // The shared track window: the union of the days' operating windows.
  let trackStart = 24;
  let trackEnd = 0;
  for (const dv of days) {
    const w = windows.get(dv) ?? null;
    trackStart = Math.min(trackStart, (w ?? DEFAULT_HOUR_RANGE).startHour);
    trackEnd = Math.max(trackEnd, (w ?? DEFAULT_HOUR_RANGE).endHour);
  }
  if (trackEnd <= trackStart) {
    trackStart = DEFAULT_HOUR_RANGE.startHour;
    trackEnd = DEFAULT_HOUR_RANGE.endHour;
  }
  const span = trackEnd - trackStart;
  const pct = (h: number) => ((h - trackStart) / span) * 100;

  const byDate = new Map<string, Reservation[]>(days.map((dv) => [dv, []]));
  for (const r of reservations) {
    const dv = localDateValueOf(r.startTime, timeZone);
    if (dv !== null) byDate.get(dv)?.push(r);
  }

  // The blocked spans per `${date}|${lane}` against the shared track (the Day grid's
  // `blockedByLane`, per day): a closed exception blocks the whole track, an open one blocks
  // the gaps its shifts leave.
  const trackWindow = { start: trackStart * 60, end: trackEnd * 60 };
  const blockedByDayLane = new Map<string, { startHour: number; endHour: number; block: DatedLaneBlock }[]>();
  for (const block of blocks) {
    const spans = blockedRangesOf(block, trackWindow).map((r) => ({ startHour: r.start / 60, endHour: r.end / 60, block }));
    if (spans.length === 0) continue;
    const key = `${block.date}|${block.laneKey}`;
    blockedByDayLane.set(key, [...(blockedByDayLane.get(key) ?? []), ...spans]);
  }

  // Capacity = the REAL lanes (Unassigned is routing, not capacity).
  const laneCount = Math.max(
    1,
    axis.lanes.filter((l) => l.key !== axis.unassignedKey).length,
  );

  const hourTicks: ReactNode[] = [];
  for (let h = trackStart; h <= trackEnd; h += 2) {
    hourTicks.push(
      <Box key={h} sx={{ position: 'absolute', left: 0, right: '8px', top: `${pct(h)}%`, textAlign: 'right' }}>
        <Typography variant="mono" sx={{ fontSize: 10, color: cm.textMuted }}>
          {compactHour(h)}
        </Typography>
      </Box>,
    );
  }

  return (
    <Box
      data-testid="week-grid"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: cm.surface,
        overflow: 'hidden',
      }}
    >
      {/* Day header cards — the load signal + the service mix as dots. */}
      <Box sx={{ display: 'flex', flex: 'none', borderBottom: `1px solid ${cm.gutterBorder}`, bgcolor: cm.tableHeaderBg }}>
        <Box sx={{ width: 44, flex: 'none', borderRight: `1px solid ${cm.gutterBorder}` }} />
        {days.map((dv) => {
          const isToday = dv === today;
          const dayBookings = (byDate.get(dv) ?? []).filter(
            (r) => deriveStatusBucket(r.status) !== 'cancelled',
          );
          const window = windows.get(dv) ?? null;
          const load = loadPercent(dayBookings, window, {
            isToday,
            nowHour,
            laneCount,
            timeZone,
          });
          const qualifier = loadQualifier(dv, today);
          const mix = new Map<string, number>();
          for (const r of dayBookings) {
            const name = r.serviceName ?? 'Reservation';
            mix.set(name, (mix.get(name) ?? 0) + 1);
          }
          // The weekday and day number come from the calendar day's digits (DNKADM-0476 F-05).
          const label = weekdayLabel(dv) ?? '';
          const dayOfMonth = ymdParts(dv)?.day ?? '';
          return (
            <Box
              key={dv}
              component="button"
              type="button"
              onClick={() => onOpenDay(dv)}
              aria-label={`Open ${label} ${dayOfMonth} — load ${load === null ? 'not measurable' : `${load}%`}, ${qualifier}`}
              sx={{
                flex: 1,
                minWidth: 0,
                font: 'inherit',
                textAlign: 'left',
                cursor: 'pointer',
                border: 0,
                borderRight: `1px solid ${cm.gutterBorder}`,
                bgcolor: isToday ? cm.todayTint : 'transparent',
                p: '9px 10px',
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
                minHeight: 74,
              }}
            >
              <Stack direction="row" sx={{ alignItems: 'center', gap: '6px', minWidth: 0, width: '100%' }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: cm.textPrimary, whiteSpace: 'nowrap' }}>
                  {label} {dayOfMonth}
                </Typography>
                {isToday && (
                  <Box aria-hidden sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: cm.nowLine, flex: 'none' }} />
                )}
                <Typography variant="mono" sx={{ fontSize: 12.5, fontWeight: 600, color: cm.textPrimary, ml: 'auto' }}>
                  {load === null ? '—' : `${load}%`}
                </Typography>
              </Stack>
              <Box sx={{ height: 5, borderRadius: 3, bgcolor: cm.neutralStatusBg, overflow: 'hidden', width: '100%' }}>
                <Box sx={{ height: '100%', width: `${load ?? 0}%`, bgcolor: cm.laneSwatch, borderRadius: 3 }} />
              </Box>
              <Stack direction="row" sx={{ gap: '6px', flexWrap: 'wrap', alignItems: 'center', minHeight: 14, width: '100%' }}>
                {[...mix.entries()].map(([name, count]) => (
                  <Stack key={name} direction="row" sx={{ alignItems: 'center', gap: '3px' }}>
                    <Box
                      aria-hidden
                      sx={{ width: 7, height: 7, borderRadius: '2px', bgcolor: deriveServiceColor(name, mode).main }}
                    />
                    <Typography variant="mono" sx={{ fontSize: 10, color: cm.textMeta }}>
                      {count}
                    </Typography>
                  </Stack>
                ))}
                {dayBookings.length === 0 && (
                  <Typography sx={{ fontSize: 10, color: cm.avgLabel }}>Nothing booked</Typography>
                )}
                <Typography sx={{ fontSize: 10, color: cm.textMuted, ml: 'auto', whiteSpace: 'nowrap' }}>
                  {load === null ? '' : qualifier}
                </Typography>
              </Stack>
            </Box>
          );
        })}
      </Box>

      {/* The mini schedule: hour gutter + 7 day columns × axis lanes. */}
      <Box sx={{ display: 'flex', height: 'clamp(300px, calc(100vh - 480px), 560px)' }}>
        <Box sx={{ width: 44, flex: 'none', position: 'relative', borderRight: `1px solid ${cm.gutterBorder}` }}>
          {hourTicks}
        </Box>
        {days.map((dv) => {
          const isToday = dv === today;
          const dayBookings = byDate.get(dv) ?? [];
          return (
            <Box
              key={dv}
              sx={{
                flex: 1,
                minWidth: 0,
                position: 'relative',
                borderRight: `1px solid ${cm.gridLine}`,
                bgcolor: isToday ? cm.todayTrackTint : 'transparent',
              }}
            >
              {Array.from({ length: Math.floor(span / 2) + 1 }, (_, i) => (
                <Box key={i} sx={{ position: 'absolute', left: 0, right: 0, top: `${pct(trackStart + i * 2)}%`, borderTop: `1px solid ${cm.gridLine}`, opacity: 0.6 }} />
              ))}
              <Box sx={{ position: 'absolute', inset: 0, display: 'flex', gap: '2px', px: '3px' }}>
                {axis.lanes.map((lane) => (
                  <Box key={lane.key} sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
                    {(blockedByDayLane.get(`${dv}|${lane.key}`) ?? []).map((b) => (
                      <Box
                        key={`blocked-${b.startHour}-${b.endHour}`}
                        {...(onBlockSelect
                          ? {
                              component: 'button' as const,
                              type: 'button' as const,
                              onClick: () => onBlockSelect(b.block, dv),
                            }
                          : { role: 'img' })}
                        aria-label={blockedAriaLabel(lane.label, b.startHour, b.endHour)}
                        data-testid="week-blocked-band"
                        style={{
                          top: `calc(${pct(b.startHour)}% + 1px)`,
                          height: `calc(${((b.endHour - b.startHour) / span) * 100}% - 2px)`,
                          left: 1,
                          right: 1,
                        }}
                        sx={{
                          ...blockedBandSx(cm),
                          p: '2px',
                          borderRadius: '3px',
                          ...(onBlockSelect ? { border: 'none', cursor: 'pointer', font: 'inherit', pointerEvents: 'auto' } : {}),
                        }}
                      />
                    ))}
                    {dayBookings
                      .filter((r) => {
                        const resolved = axis.keyOf(r);
                        const routed =
                          resolved !== axis.unassignedKey &&
                          !axis.lanes.some((l) => l.key === resolved)
                            ? axis.unassignedKey
                            : resolved;
                        return routed === lane.key;
                      })
                      .map((r) => {
                        const start = localHourOf(r.startTime, timeZone);
                        const end = localHourOf(r.endTime, timeZone);
                        if (start === null || end === null || end <= start) return null;
                        if (end <= trackStart || start >= trackEnd) return null;
                        const top = pct(Math.max(start, trackStart));
                        const height = Math.max(
                          MIN_TICK_PERCENT,
                          pct(Math.min(end, trackEnd)) - top - 0.6,
                        );
                        const bucket = deriveStatusBucket(r.status);
                        const color = deriveServiceColor(r.serviceName, mode);
                        const cancelled = bucket === 'cancelled';
                        const pending = bucket === 'pending';
                        return (
                          <Box
                            key={r.reservationUuid ?? `${r.startTime}-${lane.key}`}
                            component="button"
                            type="button"
                            onClick={() => onSelect(r)}
                            aria-label={`${r.contactName ?? r.serviceName ?? 'Reservation'}, ${r.serviceName ?? 'reservation'}${pending ? ', pending confirmation' : ''}${cancelled ? ', cancelled' : ''}`}
                            style={{
                              top: `${top}%`,
                              height: `${height}%`,
                              background: pending
                                ? 'transparent'
                                : cancelled
                                  ? color.bg
                                  : color.main,
                              border: `1px ${pending ? 'dashed' : 'solid'} ${color.main}`,
                              opacity: cancelled ? 0.45 : 1,
                            }}
                            sx={{
                              position: 'absolute',
                              // Above the blocked bands (zIndex 2), as on the Day grid: a
                              // booking that predates a block-off stays visible inside it.
                              zIndex: 3,
                              left: '1px',
                              right: '1px',
                              minHeight: 16,
                              borderRadius: '3px',
                              cursor: 'pointer',
                              p: 0,
                            }}
                          />
                        );
                      })}
                  </Box>
                ))}
              </Box>
              {isToday && nowHour > trackStart && nowHour < trackEnd && (
                <Box data-testid="week-now-line" sx={{ position: 'absolute', left: 0, right: 0, top: `${pct(nowHour)}%`, height: 2, bgcolor: cm.nowLine, zIndex: 4 }} />
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
